import { z } from "zod";
import { isoDateTimeSchema, opaqueIdSchema } from "./primitives";

/** Read-only evidence projection; deliberately excludes VIN, dealer, raw payload and costs. */
export const claimClassificationSchema = z.enum(["HISTORICAL_REAL", "SYNTHETIC"]);
export const claimSearchQuerySchema = z
  .object({
    search: z.string().trim().min(1).max(120).optional(),
    after: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    classification: claimClassificationSchema.default("HISTORICAL_REAL"),
  })
  .strict();

export const claimEvidenceSchema = z
  .object({
    id: z.string().uuid(),
    tenantId: opaqueIdSchema,
    claimNumber: z.string().max(500).nullable(),
    status: z.string().min(1).max(200),
    failureCode: z.string().max(500).nullable(),
    product: z
      .object({ id: z.string().uuid(), name: z.string().max(500) })
      .strict()
      .nullable(),
    component: z
      .object({ id: z.string().uuid(), name: z.string().max(500) })
      .strict()
      .nullable(),
    openedAt: isoDateTimeSchema.nullable(),
    closedAt: isoDateTimeSchema.nullable(),
    classification: claimClassificationSchema,
    productionUseProhibited: z.boolean(),
    provenance: z
      .object({
        lineageId: z.string().uuid(),
        sourceConnectionId: z.string().uuid(),
        ingestionRunId: z.string().uuid(),
        sourceName: z.string().min(1).max(500),
        authorityClass: z.string().min(1).max(100),
        observedAt: isoDateTimeSchema,
        effectiveAt: isoDateTimeSchema,
        quality: z.enum(["VERIFIED", "PARTIAL"]),
        reconciliation: z.enum(["PASSED", "NOT_APPLICABLE"]),
      })
      .strict(),
    warnings: z.array(z.string().min(1).max(1000)).max(20),
  })
  .strict()
  .superRefine((claim, context) => {
    if (claim.productionUseProhibited !== (claim.classification === "SYNTHETIC")) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Synthetic claim evidence must remain production-prohibited.",
      });
    }
    if (
      claim.classification === "SYNTHETIC" &&
      claim.provenance.authorityClass !== "DEMONSTRATION_ONLY"
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Synthetic evidence must retain demonstration authority.",
      });
    }
    if (
      claim.classification === "HISTORICAL_REAL" &&
      claim.provenance.reconciliation !== "PASSED"
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Historical claim evidence requires passed reconciliation.",
      });
    }
    if (
      claim.classification === "HISTORICAL_REAL" &&
      claim.provenance.authorityClass === "DEMONSTRATION_ONLY"
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Historical evidence cannot have demonstration-only authority.",
      });
    }
  });

export const claimSearchResponseSchema = z
  .object({
    tenantId: opaqueIdSchema,
    generatedAt: isoDateTimeSchema,
    query: claimSearchQuerySchema,
    items: z.array(claimEvidenceSchema).max(100),
    nextCursor: z.string().uuid().nullable(),
  })
  .strict()
  .superRefine((response, context) => {
    if (
      response.items.some(
        (item) =>
          item.tenantId !== response.tenantId ||
          item.classification !== response.query.classification,
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Claim evidence must match the selected tenant and classification.",
      });
    }
    if (
      response.items.length > response.query.limit ||
      (response.nextCursor !== null && response.nextCursor !== response.items.at(-1)?.id)
    ) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid bounded claim page." });
    }
  });
export const claimDetailResponseSchema = z
  .object({
    tenantId: opaqueIdSchema,
    generatedAt: isoDateTimeSchema,
    item: claimEvidenceSchema,
  })
  .strict()
  .superRefine((response, context) => {
    if (response.item.tenantId !== response.tenantId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Claim evidence must match the selected tenant.",
      });
    }
  });

export type ClaimSearchQuery = z.infer<typeof claimSearchQuerySchema>;
export type ClaimEvidence = z.infer<typeof claimEvidenceSchema>;
export type ClaimSearchResponse = z.infer<typeof claimSearchResponseSchema>;
export type ClaimDetailResponse = z.infer<typeof claimDetailResponseSchema>;
